import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useTransition,
} from "react";
import { useHistory, useLocation } from "react-router-dom";
import {
  AppliedFilter,
  urlParamsToFilters,
  filtersToUrlParams,
  parseLegacyFilterParam,
  whereFiltersToAppliedFilters,
  generateFilterId,
  prettifyFieldName,
} from "src/utils/filterUrlUtils";
import { FilterFieldConfig } from "src/config/filterConfig";
import { WhereFilterOperators } from "src/types/types";

interface UseFilterUrlSyncOptions {
  /** Filter configuration for the dataset */
  filterConfig?: FilterFieldConfig[];

  /** List of field names that can be filtered */
  filterableFields?: string[];

  /** Callback when filters change */
  onFiltersChange?: (filters: AppliedFilter[]) => void;

  /** Debounce delay for URL updates (ms) */
  debounceMs?: number;

  /** Whether to support legacy ?filt= JSON format */
  supportLegacyFormat?: boolean;

  /** Base path for URL (defaults to current pathname) */
  basePath?: string;
}

interface UseFilterUrlSyncReturn {
  /** Currently applied filters */
  appliedFilters: AppliedFilter[];

  /** Set all filters (replaces existing) */
  setFilters: (filters: AppliedFilter[]) => void;

  /** Add a single filter */
  addFilter: (filter: Omit<AppliedFilter, "id">) => void;

  /** Remove a filter by ID */
  removeFilter: (filterId: string) => void;

  /** Remove all filters for a field */
  removeFiltersByField: (fieldName: string) => void;

  /** Update a filter's value */
  updateFilter: (
    filterId: string,
    value: AppliedFilter["value"],
    displayValue?: string,
  ) => void;

  /** Clear all filters */
  clearFilters: () => void;

  /** Toggle a value in a multi-select filter */
  toggleFilterValue: (fieldName: string, value: string, label?: string) => void;

  /** Set a range filter */
  setRangeFilter: (
    fieldName: string,
    min: number | undefined,
    max: number | undefined,
  ) => void;

  /** Set a date range filter */
  setDateRangeFilter: (
    fieldName: string,
    after: string | undefined,
    before: string | undefined,
  ) => void;

  /** Set a text (contains/like) filter */
  setTextFilter: (fieldName: string, value: string) => void;

  /** Get text filter value for a field */
  getTextValue: (fieldName: string) => string;

  /** Check if a specific value is selected for a field */
  isValueSelected: (fieldName: string, value: string) => boolean;

  /** Get all selected values for a field */
  getSelectedValues: (fieldName: string) => string[];

  /** Get the current range for a field */
  getRangeValues: (
    fieldName: string,
  ) => { min?: number; max?: number } | undefined;

  /** Get the current date range for a field */
  getDateRangeValues: (
    fieldName: string,
  ) => { after?: string; before?: string } | undefined;

  /** Whether any filters are currently applied */
  hasActiveFilters: boolean;

  /** Count of active filters */
  activeFilterCount: number;

  /** Whether a filter update is pending (for loading states) */
  isPending: boolean;

  /**
   * Counter that increments only on user-initiated filter changes (not on
   * back/forward URL navigation). Use this as a dependency for page reset
   * effects instead of appliedFilters.length to avoid resetting page on
   * browser back/forward.
   */
  userFilterVersion: number;
}

/**
 * Compare two filter arrays for semantic equality (ignores id/displayValue differences).
 * Prevents new array references when URL changes for non-filter params (page, sort).
 */
function filtersAreEqual(a: AppliedFilter[], b: AppliedFilter[]): boolean {
  if (a.length !== b.length) return false;

  const canonicalize = (filters: AppliedFilter[]) =>
    filters
      .map((f) => {
        const val = Array.isArray(f.value)
          ? [...f.value].sort().join(",")
          : String(f.value);
        return `${f.fieldName}|${f.operator}|${val}`;
      })
      .sort()
      .join("||");

  return canonicalize(a) === canonicalize(b);
}

/**
 * Hook to synchronize filter state with URL parameters
 *
 * Features:
 * - Bidirectional sync between state and URL
 * - Support for legacy JSON filter format
 * - Debounced URL updates
 * - Browser history integration (back/forward works)
 * - Performance optimized with useTransition
 */
export function useFilterUrlSync(
  options: UseFilterUrlSyncOptions = {},
): UseFilterUrlSyncReturn {
  const {
    filterConfig = [],
    filterableFields = [],
    onFiltersChange,
    debounceMs = 300,
    supportLegacyFormat = true,
    basePath,
  } = options;

  const history = useHistory();
  const location = useLocation();

  // Use transition for non-urgent updates (reduces jank)
  const [isPending, startTransition] = useTransition();

  // Compute filterable field names (memoized)
  const allFilterableFields = useMemo(() => {
    const fromConfig = filterConfig.map((c) => c.fieldName);
    return [...new Set([...fromConfig, ...filterableFields])];
  }, [filterConfig, filterableFields]);

  // Ref to track if we're updating from our own changes
  const isInternalUpdate = useRef(false);
  const debounceTimer = useRef<NodeJS.Timeout>();
  const lastUrlRef = useRef(location.search);
  const onFiltersChangeRef = useRef(onFiltersChange);

  // Keep callback ref up to date
  useEffect(() => {
    onFiltersChangeRef.current = onFiltersChange;
  }, [onFiltersChange]);

  // Parse filters from URL (memoized for performance)
  const parseFiltersFromUrl = useCallback(
    (search: string): AppliedFilter[] => {
      const params = new URLSearchParams(search);

      // Check for legacy format first
      if (supportLegacyFormat) {
        const legacyFilt = params.get("filt");
        if (legacyFilt) {
          const legacyFilters = parseLegacyFilterParam(legacyFilt);
          if (legacyFilters.length > 0) {
            return whereFiltersToAppliedFilters(legacyFilters);
          }
        }
      }

      // Parse new format
      return urlParamsToFilters(params, allFilterableFields);
    },
    [allFilterableFields, supportLegacyFormat],
  );

  // State - initialized from URL
  const [appliedFilters, setAppliedFiltersState] = useState<AppliedFilter[]>(
    () => parseFiltersFromUrl(location.search),
  );

  // Incremented only on user-initiated filter changes (not back/forward navigation).
  // Used by consumers for page reset effects that should not trigger on browser back/forward.
  const [userFilterVersion, setUserFilterVersion] = useState(0);

  // Track if we've migrated from legacy format
  const hasMigratedLegacy = useRef(false);

  // Migrate legacy format to new format on mount (only once)
  useEffect(() => {
    if (hasMigratedLegacy.current || !supportLegacyFormat) return;

    const params = new URLSearchParams(location.search);
    const legacyFilt = params.get("filt");

    if (legacyFilt) {
      // Convert and update URL immediately without triggering re-renders
      hasMigratedLegacy.current = true;
      const currentFilters = parseFiltersFromUrl(location.search);

      if (currentFilters.length > 0) {
        // Update URL immediately (no debounce) to remove legacy format
        const filterParams = filtersToUrlParams(currentFilters);
        const preservedParams = new URLSearchParams();

        // Preserve non-filter params
        ["page", "limit", "sort", "q", "search_text"].forEach((key) => {
          const value = params.get(key);
          if (value) preservedParams.set(key, value);
        });

        // Merge filter params
        filterParams.forEach((value, key) => {
          preservedParams.set(key, value);
        });

        // Remove legacy filt param
        preservedParams.delete("filt");

        const newSearch = preservedParams.toString();
        const searchWithPrefix = newSearch ? `?${newSearch}` : "";
        const currentPath = basePath || location.pathname;

        // Use replace to avoid adding to history
        isInternalUpdate.current = true;
        lastUrlRef.current = searchWithPrefix;
        history.replace(`${currentPath}${searchWithPrefix}`);
        requestAnimationFrame(() => {
          isInternalUpdate.current = false;
        });
      }
    } else {
      hasMigratedLegacy.current = true;
    }
  }, []);

  // Update URL with debouncing
  const scheduleUrlUpdate = useCallback(
    (filters: AppliedFilter[]) => {
      // Clear existing timer
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }

      debounceTimer.current = setTimeout(() => {
        const currentParams = new URLSearchParams(location.search);
        const filterParams = filtersToUrlParams(filters);

        // Preserve non-filter params
        const preservedParams = new URLSearchParams();
        ["page", "limit", "sort", "q", "search_text"].forEach((key) => {
          const value = currentParams.get(key);
          if (value) preservedParams.set(key, value);
        });

        // Merge filter params
        filterParams.forEach((value, key) => {
          preservedParams.set(key, value);
        });

        // Remove legacy filt param
        preservedParams.delete("filt");

        const newSearch = preservedParams.toString();
        const searchWithPrefix = newSearch ? `?${newSearch}` : "";

        // Only update if URL actually changed
        if (location.search !== searchWithPrefix) {
          isInternalUpdate.current = true;
          lastUrlRef.current = searchWithPrefix;
          const currentPath = basePath || location.pathname;
          history.replace(`${currentPath}${searchWithPrefix}`);
          // Reset flag after a tick
          requestAnimationFrame(() => {
            isInternalUpdate.current = false;
          });
        }
      }, debounceMs);
    },
    [location.search, location.pathname, basePath, history, debounceMs],
  );

  // Listen for URL changes (browser back/forward only)
  useEffect(() => {
    // Skip if this was our own update
    if (isInternalUpdate.current || location.search === lastUrlRef.current) {
      return;
    }

    lastUrlRef.current = location.search;
    const newFilters = parseFiltersFromUrl(location.search);

    // Use transition for smoother updates
    startTransition(() => {
      setAppliedFiltersState((prev) => {
        // Deep compare to avoid new array references when filters haven't
        // actually changed (e.g. only page or sort changed in URL).
        // This prevents cascading re-renders that drop the page param.
        if (filtersAreEqual(prev, newFilters)) {
          return prev;
        }
        onFiltersChangeRef.current?.(newFilters);
        return newFilters;
      });
    });
  }, [location.search, parseFiltersFromUrl]);

  // Cleanup debounce timer
  useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, []);

  // ============================================================================
  // Filter manipulation methods (optimized to avoid unnecessary re-renders)
  // ============================================================================

  // Helper: schedule URL update AND bump user-initiated version counter
  const userScheduleUrlUpdate = useCallback(
    (filters: AppliedFilter[]) => {
      setUserFilterVersion((v) => v + 1);
      scheduleUrlUpdate(filters);
    },
    [scheduleUrlUpdate],
  );

  const setFilters = useCallback(
    (filters: AppliedFilter[]) => {
      setAppliedFiltersState(filters);
      userScheduleUrlUpdate(filters);
      // Call onChange after state update
      startTransition(() => {
        onFiltersChangeRef.current?.(filters);
      });
    },
    [userScheduleUrlUpdate],
  );

  const addFilter = useCallback(
    (filter: Omit<AppliedFilter, "id">) => {
      setAppliedFiltersState((prev) => {
        const newFilter: AppliedFilter = {
          ...filter,
          id: generateFilterId(),
        };

        // Check if filter for this field/operator already exists
        const existingIndex = prev.findIndex(
          (f) =>
            f.fieldName === filter.fieldName && f.operator === filter.operator,
        );

        let updated: AppliedFilter[];
        if (existingIndex >= 0) {
          // Replace existing
          updated = [...prev];
          updated[existingIndex] = newFilter;
        } else {
          updated = [...prev, newFilter];
        }

        userScheduleUrlUpdate(updated);
        startTransition(() => {
          onFiltersChangeRef.current?.(updated);
        });
        return updated;
      });
    },
    [userScheduleUrlUpdate],
  );

  const removeFilter = useCallback(
    (filterId: string) => {
      setAppliedFiltersState((prev) => {
        const updated = prev.filter((f) => f.id !== filterId);
        userScheduleUrlUpdate(updated);
        startTransition(() => {
          onFiltersChangeRef.current?.(updated);
        });
        return updated;
      });
    },
    [userScheduleUrlUpdate],
  );

  const removeFiltersByField = useCallback(
    (fieldName: string) => {
      setAppliedFiltersState((prev) => {
        const updated = prev.filter((f) => f.fieldName !== fieldName);
        userScheduleUrlUpdate(updated);
        startTransition(() => {
          onFiltersChangeRef.current?.(updated);
        });
        return updated;
      });
    },
    [userScheduleUrlUpdate],
  );

  const updateFilter = useCallback(
    (
      filterId: string,
      value: AppliedFilter["value"],
      displayValue?: string,
    ) => {
      setAppliedFiltersState((prev) => {
        const updated = prev.map((f) => {
          if (f.id === filterId) {
            return {
              ...f,
              value,
              displayValue: displayValue ?? f.displayValue,
            };
          }
          return f;
        });
        userScheduleUrlUpdate(updated);
        startTransition(() => {
          onFiltersChangeRef.current?.(updated);
        });
        return updated;
      });
    },
    [userScheduleUrlUpdate],
  );

  const clearFilters = useCallback(() => {
    setAppliedFiltersState([]);
    userScheduleUrlUpdate([]);
    startTransition(() => {
      onFiltersChangeRef.current?.([]);
    });
  }, [userScheduleUrlUpdate]);

  const toggleFilterValue = useCallback(
    (fieldName: string, value: string, label?: string) => {
      setAppliedFiltersState((prev) => {
        // Find existing filter for this field
        const existingFilter = prev.find(
          (f) => f.fieldName === fieldName && f.operator === "eq",
        );

        let updated: AppliedFilter[];

        if (existingFilter) {
          const currentValues = Array.isArray(existingFilter.value)
            ? existingFilter.value
            : [existingFilter.value];

          const isSelected = currentValues.includes(value);

          if (isSelected) {
            // Remove value
            const newValues = currentValues.filter((v) => v !== value);

            if (newValues.length === 0) {
              // Remove filter entirely
              updated = prev.filter((f) => f.id !== existingFilter.id);
            } else {
              // Update with remaining values
              updated = prev.map((f) => {
                if (f.id === existingFilter.id) {
                  return {
                    ...f,
                    value: newValues.length === 1 ? newValues[0] : newValues,
                    displayValue: newValues.join(", "),
                  };
                }
                return f;
              });
            }
          } else {
            // Add value
            const newValues = [...currentValues, value];
            updated = prev.map((f) => {
              if (f.id === existingFilter.id) {
                return {
                  ...f,
                  value: newValues,
                  displayValue: newValues.join(", "),
                };
              }
              return f;
            });
          }
        } else {
          // Create new filter
          const newFilter: AppliedFilter = {
            id: generateFilterId(),
            fieldName,
            operator: "eq" as WhereFilterOperators,
            value,
            displayLabel: label || prettifyFieldName(fieldName),
            displayValue: value,
          };
          updated = [...prev, newFilter];
        }

        userScheduleUrlUpdate(updated);
        startTransition(() => {
          onFiltersChangeRef.current?.(updated);
        });
        return updated;
      });
    },
    [userScheduleUrlUpdate],
  );

  const setRangeFilter = useCallback(
    (fieldName: string, min: number | undefined, max: number | undefined) => {
      setAppliedFiltersState((prev) => {
        // Remove existing range filters for this field
        let updated = prev.filter(
          (f) =>
            f.fieldName !== fieldName ||
            (f.operator !== "ge" && f.operator !== "le"),
        );

        // Add new range filters
        if (min !== undefined) {
          updated.push({
            id: generateFilterId(),
            fieldName,
            operator: "ge" as WhereFilterOperators,
            value: String(min),
            displayLabel: prettifyFieldName(fieldName),
            displayValue: `≥ ${min}`,
          });
        }

        if (max !== undefined) {
          updated.push({
            id: generateFilterId(),
            fieldName,
            operator: "le" as WhereFilterOperators,
            value: String(max),
            displayLabel: prettifyFieldName(fieldName),
            displayValue: `≤ ${max}`,
          });
        }

        userScheduleUrlUpdate(updated);
        startTransition(() => {
          onFiltersChangeRef.current?.(updated);
        });
        return updated;
      });
    },
    [userScheduleUrlUpdate],
  );

  const setDateRangeFilter = useCallback(
    (
      fieldName: string,
      after: string | undefined,
      before: string | undefined,
    ) => {
      setAppliedFiltersState((prev) => {
        // Remove existing date range filters for this field
        let updated = prev.filter(
          (f) =>
            f.fieldName !== fieldName ||
            (f.operator !== "ge" && f.operator !== "le"),
        );

        // Add new date range filters
        if (after !== undefined) {
          updated.push({
            id: generateFilterId(),
            fieldName,
            operator: "ge" as WhereFilterOperators,
            value: after,
            displayLabel: prettifyFieldName(fieldName),
            displayValue: `After ${after}`,
          });
        }

        if (before !== undefined) {
          updated.push({
            id: generateFilterId(),
            fieldName,
            operator: "le" as WhereFilterOperators,
            value: before,
            displayLabel: prettifyFieldName(fieldName),
            displayValue: `Before ${before}`,
          });
        }

        userScheduleUrlUpdate(updated);
        startTransition(() => {
          onFiltersChangeRef.current?.(updated);
        });
        return updated;
      });
    },
    [userScheduleUrlUpdate],
  );

  const setTextFilter = useCallback(
    (fieldName: string, value: string) => {
      setAppliedFiltersState((prev) => {
        // Remove existing text filter for this field
        let updated = prev.filter(
          (f) => f.fieldName !== fieldName || f.operator !== "like",
        );

        // Add new text filter if value is not empty
        if (value.trim()) {
          updated.push({
            id: generateFilterId(),
            fieldName,
            operator: "like" as WhereFilterOperators,
            value: value.trim(),
            displayLabel: prettifyFieldName(fieldName),
            displayValue: `Contains "${value.trim()}"`,
          });
        }

        userScheduleUrlUpdate(updated);
        startTransition(() => {
          onFiltersChangeRef.current?.(updated);
        });
        return updated;
      });
    },
    [userScheduleUrlUpdate],
  );

  // ============================================================================
  // Query methods (memoized for performance)
  // ============================================================================

  const isValueSelected = useCallback(
    (fieldName: string, value: string): boolean => {
      const filter = appliedFilters.find(
        (f) => f.fieldName === fieldName && f.operator === "eq",
      );
      if (!filter) return false;

      const values = Array.isArray(filter.value)
        ? filter.value
        : [filter.value];
      return values.includes(value);
    },
    [appliedFilters],
  );

  const getSelectedValues = useCallback(
    (fieldName: string): string[] => {
      const filter = appliedFilters.find(
        (f) => f.fieldName === fieldName && f.operator === "eq",
      );
      if (!filter) return [];

      return Array.isArray(filter.value) ? filter.value : [filter.value];
    },
    [appliedFilters],
  );

  const getRangeValues = useCallback(
    (fieldName: string): { min?: number; max?: number } | undefined => {
      const geFilter = appliedFilters.find(
        (f) => f.fieldName === fieldName && f.operator === "ge",
      );
      const leFilter = appliedFilters.find(
        (f) => f.fieldName === fieldName && f.operator === "le",
      );

      if (!geFilter && !leFilter) return undefined;

      return {
        min: geFilter ? Number(geFilter.value) : undefined,
        max: leFilter ? Number(leFilter.value) : undefined,
      };
    },
    [appliedFilters],
  );

  const getDateRangeValues = useCallback(
    (fieldName: string): { after?: string; before?: string } | undefined => {
      const geFilter = appliedFilters.find(
        (f) => f.fieldName === fieldName && f.operator === "ge",
      );
      const leFilter = appliedFilters.find(
        (f) => f.fieldName === fieldName && f.operator === "le",
      );

      if (!geFilter && !leFilter) return undefined;

      return {
        after: geFilter ? String(geFilter.value) : undefined,
        before: leFilter ? String(leFilter.value) : undefined,
      };
    },
    [appliedFilters],
  );

  const getTextValue = useCallback(
    (fieldName: string): string => {
      const filter = appliedFilters.find(
        (f) => f.fieldName === fieldName && f.operator === "like",
      );
      return filter ? String(filter.value) : "";
    },
    [appliedFilters],
  );

  // Computed values
  const hasActiveFilters = appliedFilters.length > 0;
  const activeFilterCount = appliedFilters.length;

  return {
    appliedFilters,
    setFilters,
    addFilter,
    removeFilter,
    removeFiltersByField,
    updateFilter,
    clearFilters,
    toggleFilterValue,
    setRangeFilter,
    setDateRangeFilter,
    setTextFilter,
    isValueSelected,
    getSelectedValues,
    getRangeValues,
    getDateRangeValues,
    getTextValue,
    hasActiveFilters,
    activeFilterCount,
    isPending,
    userFilterVersion,
  };
}
